import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './dialog'

type ModalProps = {
  open: boolean
  title?: string
  children: ReactNode
  onClose?: () => void
  footer?: ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl'
  closeOnEscape?: boolean
  closeOnOverlayClick?: boolean
  showCloseButton?: boolean
}

export function Modal({
  open,
  title,
  children,
  onClose,
  footer,
  size = 'md',
  closeOnEscape = true,
  closeOnOverlayClick = true,
  showCloseButton = true,
}: ModalProps) {
  const maxW =
    size === 'sm'
      ? 'max-w-md'
      : size === 'lg'
        ? 'max-w-2xl'
        : size === 'xl'
          ? 'max-w-4xl'
          : 'max-w-lg'

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose?.()
      }}
    >
      <DialogContent
        showCloseButton={Boolean(showCloseButton && onClose)}
        className={cn(maxW, 'gap-0 p-6 sm:p-8')}
        onEscapeKeyDown={(e) => {
          if (!closeOnEscape) e.preventDefault()
        }}
        onPointerDownOutside={(e) => {
          if (!closeOnOverlayClick) e.preventDefault()
        }}
        onInteractOutside={(e) => {
          if (!closeOnOverlayClick) e.preventDefault()
        }}
      >
        <DialogHeader className={title ? 'mb-5 text-left' : 'sr-only'}>
          <DialogTitle id={title ? 'modal-title' : undefined}>{title ?? 'Dialog'}</DialogTitle>
        </DialogHeader>
        <div className="text-sm leading-relaxed text-muted-foreground">{children}</div>
        {footer ? <DialogFooter className="mt-8 border-t border-border pt-6">{footer}</DialogFooter> : null}
      </DialogContent>
    </Dialog>
  )
}
